import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { dashboardApi, DashboardStats } from '@/services/api/dashboard';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users,
  FileText,
  FolderOpen,
  MessageSquare,
  ThumbsUp,
  Clock,
  TrendingUp,
  Search,
  AlertCircle,
  CheckCircle,
  XCircle,
  HelpCircle,
  BookOpen,
  LayoutDashboard,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatDistanceToNow } from 'date-fns';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
}

function StatCard({ title, value, description, icon: Icon, trend, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 p-2 sm:p-3 sm:pb-1">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-3" />
        </CardHeader>
        <CardContent className="p-2 pt-0 sm:p-3 sm:pt-0">
          <Skeleton className="h-6 w-14 mb-0.5" />
          <Skeleton className="h-2.5 w-16" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 p-2 sm:p-3 sm:pb-1">
        <CardTitle className="text-[10px] sm:text-xs font-medium truncate pr-2">{title}</CardTitle>
        <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />
      </CardHeader>
      <CardContent className="p-2 pt-0 sm:p-3 sm:pt-0">
        <div className="text-lg sm:text-xl font-bold">{value}</div>
        {(description || trend) && (
          <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
            {trend && (
              <span
                className={
                  trend.isPositive ? 'text-green-500' : 'text-red-500'
                }
              >
                {trend.isPositive ? '+' : ''}
                {trend.value}%{' '}
              </span>
            )}
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

export function DashboardPage() {
  const { user } = useAuthStore();

  const { data, isLoading, error } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
    refetchInterval: 60000, // Refresh every minute
  });

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'super_admin':
        return 'Super Admin';
      case 'tenant_admin':
        return 'Tenant Admin';
      case 'manager':
        return 'Manager';
      default:
        return 'User';
    }
  };

  const stats = data?.stats;
  const charts = data?.charts;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Welcome Section */}
      <PageHeader
        icon={LayoutDashboard}
        title={`Welcome back, ${user?.firstName || 'Admin'}!`}
        description="Here's what's happening with your Knowledge Base today."
        actions={
          <Badge variant={user?.role === 'super_admin' ? 'destructive' : 'default'} className="w-fit">
            {getRoleLabel()}
          </Badge>
        }
      />


      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 p-4">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">Failed to load dashboard data. Please try again.</span>
          </CardContent>
        </Card>
      )}

      {/* Primary Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Documents"
          value={stats?.documents.total ?? 0}
          description={`${stats?.documents.indexed ?? 0} indexed`}
          icon={FileText}
          loading={isLoading}
        />
        <StatCard
          title="Knowledge Chunks"
          value={formatNumber(stats?.knowledgeBase.totalChunks ?? 0)}
          description="embedded vectors"
          icon={FolderOpen}
          loading={isLoading}
        />
        <StatCard
          title="Total Queries"
          value={formatNumber(stats?.queries.total ?? 0)}
          description={`${stats?.queries.today ?? 0} today`}
          icon={MessageSquare}
          loading={isLoading}
        />
        <StatCard
          title="Avg Response Time"
          value={`${stats?.queries.avgResponseTime ?? 0}ms`}
          description={`${((stats?.queries.successRate ?? 0) * 100).toFixed(0)}% success rate`}
          icon={Clock}
          loading={isLoading}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Document Topics"
          value={stats?.documents.uniqueTopics ?? 0}
          description="unique categories"
          icon={BookOpen}
          loading={isLoading}
        />
        <StatCard
          title="Storage Used"
          value={formatBytes(stats?.documents.totalSizeBytes ?? 0)}
          description="total file size"
          icon={FolderOpen}
          loading={isLoading}
        />
        <StatCard
          title="User Feedback"
          value={stats?.feedback.total ?? 0}
          description={stats?.feedback.satisfactionRate
            ? `${(stats.feedback.satisfactionRate * 100).toFixed(0)}% positive`
            : 'no ratings yet'}
          icon={ThumbsUp}
          loading={isLoading}
        />
        <StatCard
          title="Active Users"
          value={stats?.users.active ?? 0}
          description={`of ${stats?.users.total ?? 0} total`}
          icon={Users}
          loading={isLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Query Trend Chart */}
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Query Activity (Last 7 Days)
            </CardTitle>
            <CardDescription>Number of queries per day</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 pb-4">
            {isLoading ? (
              <Skeleton className="h-full w-full min-h-[200px]" />
            ) : charts?.queryTrend && charts.queryTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <AreaChart data={charts.queryTrend}>
                  <defs>
                    <linearGradient id="colorQueries" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="queries"
                    stroke="#8884d8"
                    fillOpacity={1}
                    fill="url(#colorQueries)"
                    name="Queries"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Search className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No query data yet</p>
                  <p className="text-sm">Start using the KB Chat to see analytics</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Topics Table */}
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Top Query Topics
            </CardTitle>
            <CardDescription>Most frequently asked topics (Top 10)</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 pb-4">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : charts?.topTopics && charts.topTopics.length > 0 ? (
              <div className="space-y-2">
                {charts.topTopics.slice(0, 10).map((item, index) => (
                  <div
                    key={item.topic}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-medium shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium truncate">{item.topic}</span>
                    </div>
                    <Badge variant="secondary" className="shrink-0 ml-2">
                      {item.count} queries
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Search className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No topic data yet</p>
                  <p className="text-sm">Topics will appear after users ask questions</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        {/* Most Accessed Documents */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Most Accessed Documents
            </CardTitle>
            <CardDescription>Documents most frequently used to answer queries</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : charts?.topDocuments && charts.topDocuments.length > 0 ? (
              <div className="space-y-3">
                {charts.topDocuments.slice(0, 8).map((doc, index) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-medium">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{doc.filename}</p>
                        {doc.topic && (
                          <p className="text-xs text-muted-foreground truncate">{doc.topic}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <p className="font-medium">{doc.accessCount} uses</p>
                      <p className="text-xs text-muted-foreground">
                        {(doc.avgSimilarity * 100).toFixed(0)}% relevance
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No document access data yet</p>
                  <p className="text-sm">
                    <Link to="/knowledge-base" className="text-primary hover:underline">
                      Upload documents
                    </Link>
                    {' '}to get started
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ThumbsUp className="h-5 w-5" />
              Feedback Distribution
            </CardTitle>
            <CardDescription>User ratings on KB responses</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : charts?.feedbackDistribution && charts.feedbackDistribution.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={charts.feedbackDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="value"
                      nameKey="name"
                    >
                      {charts.feedbackDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {charts.feedbackDistribution.map((item) => (
                    <div key={item.name} className="flex items-center gap-2 text-sm">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="truncate">{item.name}</span>
                      <span className="text-muted-foreground ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <ThumbsUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No feedback yet</p>
                  <p className="text-sm">Users can rate responses in KB Chat</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Queries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Recent Queries
          </CardTitle>
          <CardDescription>Latest questions asked to the Knowledge Base</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : data?.recentQueries && data.recentQueries.length > 0 ? (
            <div className="space-y-3">
              {data.recentQueries.map((query) => (
                <div
                  key={query.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {query.success ? (
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      <p className="font-medium truncate">{query.queryText}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {query.topic && (
                        <Badge variant="secondary" className="text-xs">
                          {query.topic}
                        </Badge>
                      )}
                      <span>{query.docsUsed} docs used</span>
                      <span>{query.responseTime}ms</span>
                      <span>{formatDistanceToNow(new Date(query.createdAt), { addSuffix: true })}</span>
                    </div>
                  </div>
                  <div className="shrink-0 ml-4">
                    {query.feedback === 'helpful' && (
                      <Badge variant="default" className="bg-green-500">
                        <ThumbsUp className="h-3 w-3 mr-1" /> Helpful
                      </Badge>
                    )}
                    {query.feedback === 'not_helpful' && (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" /> Not Helpful
                      </Badge>
                    )}
                    {query.feedback === 'incorrect' && (
                      <Badge variant="default" className="bg-orange-500">
                        <AlertCircle className="h-3 w-3 mr-1" /> Incorrect
                      </Badge>
                    )}
                    {query.feedback === 'incomplete' && (
                      <Badge variant="default" className="bg-yellow-500">
                        <HelpCircle className="h-3 w-3 mr-1" /> Incomplete
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No queries yet</p>
                <p className="text-sm">
                  <Link to="/knowledge-base/chat" className="text-primary hover:underline">
                    Try the KB Chat
                  </Link>
                  {' '}to test your Knowledge Base
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
